import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DimensionValueGroupingToken, EnumToken } from '@sleed/bizcommerce-api';
import { dimensionValueGroupingApi } from '~/lib/api/client';
import { formToToken, type DimensionGroupingFormValues } from '~/lib/schemas/dimension-grouping';

export const dimensionGroupingKeys = {
	all: ['dimension-groupings'] as const,
	list: () => [...dimensionGroupingKeys.all, 'list'] as const,
	types: () => [...dimensionGroupingKeys.all, 'types'] as const
};

export function useDimensionGroupingsList() {
	return useQuery<DimensionValueGroupingToken[]>({
		queryKey: dimensionGroupingKeys.list(),
		queryFn: async () => (await dimensionValueGroupingApi.getAll()).data.items ?? []
	});
}

/** Grouping-type lookup (Footwear/Clothing/…) for the type column + edit dropdown. */
export function useDimensionGroupingTypes() {
	return useQuery<EnumToken[]>({
		queryKey: dimensionGroupingKeys.types(),
		queryFn: async () => (await dimensionValueGroupingApi.getDimenstionValueGroupingTypes()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useDimensionGroupingMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: dimensionGroupingKeys.list() });
	const create = useMutation({
		mutationFn: async (form: DimensionGroupingFormValues) => (await dimensionValueGroupingApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: DimensionGroupingFormValues) => (await dimensionValueGroupingApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await dimensionValueGroupingApi.remove(id)).data,
		onSuccess: invalidate
	});
	return { create, update, remove };
}
